import { useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Paperclip, X } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, Skeleton, Textarea } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { portalKeys, replyToTicket, ticketFileHref, usePortalTicket } from '../api';
import { TicketCardView } from './ChatCards';
import styles from './Questions.module.css';

const MAX_FILES = 3;

/**
 * „Ticket im Portal öffnen" (Portal 26): the ticket card, the public thread and the answer with up to three files
 * (`POST /api/portal/tickets/:id/messages`). A closed ticket takes no answer (`canReply`). Bottom sheet below 768 px.
 */
export function TicketSheet({ ticketId, onClose }: { ticketId: string | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const detail = usePortalTicket(ticketId);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  function close() {
    setText('');
    setFiles([]);
    setError(null);
    onClose();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ticketId || !text.trim()) return;
    setPending(true);
    setError(null);
    try {
      const next = await replyToTicket(ticketId, text.trim(), files);
      queryClient.setQueryData(portalKeys.ticket(ticketId), next);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: portalKeys.tickets, exact: true }),
        queryClient.invalidateQueries({ queryKey: portalKeys.chat }),
      ]);
      setText('');
      setFiles([]);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  const data = detail.data;
  return (
    <Dialog
      open={ticketId !== null}
      onClose={close}
      title={data ? data.card.title : 'Ihr Anliegen'}
      eyebrow={data ? `Ticket ${data.card.numberLabel}` : undefined}
      width={640}
      overlayTop={0}
      footer={
        data?.canReply ? (
          <Button type="submit" form="portal-ticket-reply" loading={pending} disabled={!text.trim()}>
            Antwort senden
          </Button>
        ) : (
          <Button variant="secondary" onClick={close}>
            Schließen
          </Button>
        )
      }
    >
      {detail.isPending ? (
        <div className={styles.sheetSkeleton} aria-busy="true">
          <Skeleton height={140} />
          <Skeleton height={80} />
        </div>
      ) : detail.isError ? (
        <Banner tone="danger">Das Ticket konnte nicht geladen werden. {errorMessage(detail.error)}</Banner>
      ) : data ? (
        <div className={styles.sheetBody}>
          <TicketCardView ticket={data.card} />
          <ol className={styles.thread} aria-label="Verlauf">
            {data.thread.map((entry) => (
              <li
                key={entry.id}
                className={clsx(styles.threadEntry, entry.fromCustomer && styles.threadMine)}
              >
                <div className={styles.threadMeta}>
                  {entry.senderName} · {entry.atLabel}
                </div>
                <div className={styles.threadText}>{entry.text}</div>
                {entry.attachments.length > 0 ? (
                  <div className={styles.threadFiles}>
                    {entry.attachments.map((file) => (
                      <a
                        key={file.fileId}
                        className={styles.threadFile}
                        href={ticketFileHref(data.card.id, file.fileId)}
                        download
                      >
                        <Paperclip size={14} strokeWidth={1.75} aria-hidden="true" />
                        {file.name}
                      </a>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
          {data.canReply ? (
            <form
              id="portal-ticket-reply"
              className={styles.replyForm}
              onSubmit={(event) => void submit(event)}
            >
              {error ? <Banner tone="danger">{error}</Banner> : null}
              <Textarea
                label="Ihre Antwort"
                value={text}
                onChange={(event) => setText(event.target.value)}
                autoGrow
                rows={3}
              />
              <div className={styles.replyFiles}>
                {files.map((file, index) => (
                  <span key={`${file.name}-${index}`} className={styles.fileChip}>
                    <Paperclip size={14} strokeWidth={1.75} aria-hidden="true" />
                    {file.name}
                    <button
                      type="button"
                      className={styles.fileChipRemove}
                      aria-label={`${file.name} entfernen`}
                      onClick={() =>
                        setFiles((current) => current.filter((_, position) => position !== index))
                      }
                    >
                      <X size={14} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </span>
                ))}
                {files.length < MAX_FILES ? (
                  <button type="button" className={styles.cardLink} onClick={() => picker.current?.click()}>
                    Datei hinzufügen
                  </button>
                ) : null}
                <input
                  ref={picker}
                  type="file"
                  className={styles.srOnly}
                  tabIndex={-1}
                  aria-hidden="true"
                  accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(event) => {
                    const picked = Array.from(event.target.files ?? []);
                    event.target.value = '';
                    setFiles((current) => [...current, ...picked].slice(0, MAX_FILES));
                  }}
                />
              </div>
            </form>
          ) : (
            <p className={styles.closedNote}>Dieses Ticket ist abgeschlossen.</p>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
